(() => {
  const money = (cents, currency = 'USD') => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (_) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const initPurchaseOptions = (scope = document) => {
    scope.querySelectorAll('[data-utsu-purchase-options]').forEach((fieldset) => {
      if (fieldset.dataset.utsuInitialized === 'true') return;
      fieldset.dataset.utsuInitialized = 'true';

      const root = fieldset.closest('[data-utsu-product-root]');
      const variantSelect = root?.querySelector('[data-utsu-variant-select]');
      const planSelect = fieldset.querySelector('[data-utsu-selling-plan-select]');
      const planInput = fieldset.querySelector('[data-utsu-selling-plan-input]');
      const planFrequency = fieldset.querySelector('[data-utsu-plan-frequency]');
      const subscriptionPrice = fieldset.querySelector('[data-utsu-subscription-price]');
      const subscriptionLabel = fieldset.querySelector('[data-utsu-subscription-label]');
      const oneTimePrice = fieldset.querySelector('[data-utsu-one-time-price]');
      const stickyPrice = root?.querySelector('[data-utsu-sticky-price]');
      const radios = [...fieldset.querySelectorAll('[data-utsu-purchase-choice]')];
      const dataNode = fieldset.querySelector('[data-utsu-selling-plan-data]');
      if (!planSelect || !dataNode || !radios.length) return;

      let variants = [];
      try { variants = JSON.parse(dataNode.textContent || '[]'); } catch (_) { return; }

      const selectedVariantId = () => String(variantSelect?.value || variants[0]?.id || '');
      const selectedChoice = () => radios.find((radio) => radio.checked)?.value || 'one_time';

      const setCardState = () => {
        radios.forEach((radio) => radio.closest('.utsu-purchase-option')?.classList.toggle('is-selected', radio.checked));
      };

      const syncSellingPlan = () => {
        const value = selectedChoice() === 'subscription' && !planSelect.disabled ? planSelect.value : '';
        if (!planInput) return;
        planInput.value = value;
        planInput.dispatchEvent(new Event('change', { bubbles: true }));
      };

      const renderVariant = () => {
        const previousPlan = planSelect.value;
        const record = variants.find((variant) => String(variant.id) === selectedVariantId()) || variants[0];
        if (!record) return;
        oneTimePrice.textContent = record.priceFormatted || oneTimePrice.textContent;
        planSelect.innerHTML = '';
        (record.plans || []).forEach((plan) => {
          const option = document.createElement('option');
          option.value = plan.id;
          option.textContent = `${plan.name} — ${plan.priceFormatted}`;
          option.dataset.price = plan.priceFormatted;
          planSelect.append(option);
        });

        if ([...planSelect.options].some((option) => option.value === previousPlan)) {
          planSelect.value = previousPlan;
        }

        const subscriptionRadio = radios.find((radio) => radio.value === 'subscription');
        const hasPlans = planSelect.options.length > 0;
        if (subscriptionRadio) subscriptionRadio.disabled = !hasPlans;
        if (hasPlans) {
          subscriptionPrice.textContent = planSelect.options[0].dataset.price || '';
          const firstPlan = record.plans[0];
          const discount = record.price > 0 ? Math.round(((record.price - firstPlan.price) / record.price) * 100) : 0;
          if (subscriptionLabel) subscriptionLabel.textContent = discount > 0 ? `Subscribe & save ${discount}%` : 'Subscribe';
        }

        if (!hasPlans && selectedChoice() === 'subscription') {
          const oneTimeRadio = radios.find((radio) => radio.value === 'one_time');
          if (oneTimeRadio) oneTimeRadio.checked = true;
        }
        const subscribing = hasPlans && selectedChoice() === 'subscription';
        planSelect.disabled = !subscribing;
        planFrequency.hidden = !subscribing;
        if (stickyPrice) {
          const selectedPlan = planSelect.options[planSelect.selectedIndex] || planSelect.options[0];
          stickyPrice.textContent = subscribing
            ? (selectedPlan?.dataset.price || record.priceFormatted || '')
            : (record.priceFormatted || '');
        }
        setCardState();
        syncSellingPlan();
      };

      radios.forEach((radio) => radio.addEventListener('change', renderVariant));
      planSelect.addEventListener('change', () => {
        syncSellingPlan();
        const option = planSelect.options[planSelect.selectedIndex];
        if (option) {
          subscriptionPrice.textContent = option.dataset.price || '';
          if (stickyPrice && selectedChoice() === 'subscription') {
            stickyPrice.textContent = option.dataset.price || '';
          }
        }
      });
      variantSelect?.addEventListener('change', renderVariant);
      renderVariant();
    });
  };

  const initBundleBuilders = (scope = document) => {
    scope.querySelectorAll('[data-utsu-bundle-builder]').forEach((builder) => {
      if (builder.dataset.utsuInitialized === 'true') return;
      builder.dataset.utsuInitialized = 'true';

      const picker = builder.querySelector('[data-utsu-bundle-tallow]');
      const image = builder.querySelector('.utsu-build-card__product-image');
      const title = builder.querySelector('[data-utsu-bundle-tallow-title]');
      const price = builder.querySelector('[data-utsu-bundle-tallow-price]');
      const total = builder.querySelector('[data-utsu-bundle-total]');
      const shipping = builder.querySelector('[data-utsu-bundle-shipping]');
      const button = builder.querySelector('[data-utsu-bundle-add]');
      const error = builder.querySelector('[data-utsu-bundle-error]');
      if (!picker || !button) return;

      const currency = builder.dataset.currency || 'USD';
      const threshold = 12500;
      const fixedTotal = Number(button.dataset.nomiPrice || 0) + Number(button.dataset.squalanePrice || 0);

      const updateBundle = () => {
        const option = picker.options[picker.selectedIndex];
        if (!option) return;
        if (title) title.textContent = option.dataset.title || option.textContent;
        if (price) price.textContent = option.dataset.priceFormatted || money(option.dataset.price, currency);
        if (image && option.dataset.image) {
          image.src = option.dataset.image;
          image.srcset = '';
          image.alt = option.dataset.imageAlt || option.dataset.title || '';
        }
        const bundleTotal = fixedTotal + Number(option.dataset.price || 0);
        if (total) total.textContent = money(bundleTotal, currency);
        if (shipping) {
          shipping.textContent = bundleTotal >= threshold
            ? 'Qualifies for complimentary standard shipping.'
            : `Add ${money(threshold - bundleTotal, currency)} more for complimentary standard shipping.`;
        }
      };

      picker.addEventListener('change', updateBundle);
      updateBundle();

      button.addEventListener('click', async () => {
        const option = picker.options[picker.selectedIndex];
        if (!option) return;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        const originalText = button.textContent;
        button.textContent = 'Adding the ritual…';
        if (error) error.textContent = '';

        const root = window.Shopify?.routes?.root || '/';
        try {
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [
                { id: Number(option.value), quantity: 1 },
                { id: Number(button.dataset.nomiVariant), quantity: 1 },
                { id: Number(button.dataset.squalaneVariant), quantity: 1 }
              ]
            })
          });
          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.description || 'The complete ritual could not be added.');
          }
          window.location.assign(`${root}cart`);
        } catch (requestError) {
          if (error) error.textContent = requestError.message || 'Please add each product separately.';
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = originalText;
        }
      });
    });
  };

  const init = (scope = document) => {
    initPurchaseOptions(scope);
    initBundleBuilders(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

